
import { Link } from 'react-router-dom';
import React from 'react';

interface ArticleHeroProps {
  title: string;
  category: string;
  readTime: string;
  date: string;
  heroImage: string;
  author: string;
  authorRole: string;
  authorImage: string;
}

/**
 * ArticleHero component – displays the hero section of an article.
 * Includes defensive checks and minimal error‑handling to keep the UI stable
 * even when some props are missing or malformed.
 */
export default function ArticleHero({
  title = '',
  category = '',
  readTime = '',
  date = '',
  heroImage = '',
  author = '',
  authorRole = '',
  authorImage = '',
}: Partial<ArticleHeroProps>) {
  // Map article categories to Tailwind colour utilities.
  const getCategoryColor = (cat: string) => {
    const colors: Record<string, string> = {
      'Social Media': 'bg-pink-100 text-pink-700',
      'Content Strategy': 'bg-emerald-100 text-emerald-700',
      SEO: 'bg-sky-100 text-sky-700',
      'Paid Ads': 'bg-amber-100 text-amber-700',
      Analytics: 'bg-violet-100 text-violet-700',
      Branding: 'bg-rose-100 text-rose-700',
    };
    return colors[cat] || 'bg-slate-100 text-slate-700';
  };

  // Guard against completely missing required data.
  const safeTitle = title || 'Untitled article';
  const safeCategory = category || 'Uncategorized';
  const safeReadTime = readTime || '0 min';
  const safeDate = date || new Date().toLocaleDateString();
  const safeHeroImage = heroImage || '/placeholder-hero.jpg';
  const safeAuthor = author || 'Anonymous';
  const safeAuthorRole = authorRole || '';
  const safeAuthorImage = authorImage || '/placeholder-author.jpg';

  return (
    <section className="pt-24 pb-12 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-[#00989e] transition-colors mb-8 cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span className="text-sm font-medium">Back to Blog</span>
        </Link>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span
            className={`px-4 py-1.5 text-sm font-semibold rounded-full ${getCategoryColor(
              safeCategory,
            )}`}
          >
            {safeCategory}
          </span>
          <span className="text-slate-400 text-sm">{safeReadTime} read</span>
          <span className="text-slate-300">•</span>
          <span className="text-slate-400 text-sm">{safeDate}</span>
        </div>

        {/* The Tailwind arbitrary value syntax (font-['Montserrat']) must be wrapped in a JSX expression */}
        <h1
          className={
            "font-['Montserrat'] text-3xl md:text-4xl lg:text-5xl font-bold text-slate-800 leading-tight mb-8"
          }
        >
          {safeTitle}
        </h1>

        <div className="flex items-center gap-4 mb-10">
          <img
            src={safeAuthorImage}
            alt={safeAuthor}
            className="w-14 h-14 rounded-full object-cover object-top border-2 border-white shadow-md"
          />
          <div>
            <p className="font-semibold text-slate-800">{safeAuthor}</p>
            {safeAuthorRole && (
              <p className="text-sm text-slate-500">{safeAuthorRole}</p>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="relative rounded-2xl overflow-hidden shadow-2xl">
          <img
            src={safeHeroImage}
            alt={safeTitle}
            className="w-full h-64 md:h-96 lg:h-[480px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        </div>
      </div>
    </section>
  );
}
